<template lang="html">
  <div class="plp-shell flex-1 bg-elevated">
    <div class="mx-auto w-full max-w-(--ui-container) px-4 py-5 sm:px-6 lg:py-6">
      <CategoryBreadcrumbs
        v-if="category"
        class="mb-3"
        :node="category"
        :by-ids="byIds"
      />

      <div class="plp-shell__title mb-4 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <h1 class="text-2xl font-bold tracking-tight text-highlighted sm:text-3xl">
          {{ title }}
        </h1>
        <span
          v-if="resultCountLabel"
          class="plp-shell__count text-sm font-medium text-muted sm:text-base"
        >{{ resultCountLabel }}</span>
      </div>

      <CatalogShowcase
        class="plp-shell__showcase"
        :category="category"
        :search-query="searchQuery"
        @update:result-count="onResultCount"
        @update:has-more="onHasMore"
      >
        <template #empty="{ reset, hasActiveFilters }">
          <NonePanel>
            <template #text>
              <div class="flex flex-col items-center gap-3 text-center">
                <p class="text-xl font-bold">
                  {{ emptyMessage }}
                </p>
                <p class="max-w-md text-sm text-muted">
                  {{ emptyHint }}
                </p>
                <div class="mt-2 flex flex-wrap items-center justify-center gap-3">
                  <UButton
                    v-if="hasActiveFilters"
                    color="primary"
                    variant="soft"
                    @click="reset"
                  >
                    Сбросить фильтры
                  </UButton>
                  <UButton
                    :to="backRoute"
                    color="neutral"
                    variant="outline"
                  >
                    {{ backLabel }}
                  </UButton>
                </div>
              </div>
            </template>
          </NonePanel>
        </template>
      </CatalogShowcase>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Shared PLP shell (Phase 7 / Marketplace Retail / R4).
 * One composition for leaf category listings and optional `/catalog?q=` (P1).
 * Listing engine stays CatalogShowcase; this file owns chrome: crumbs, H1+count, empty CTA.
 * No cross-category facet sidebar (S21 out / D14).
 */
import { pluralizeProduct } from '#Order/utils/format'
import CategoryBreadcrumbs from '#Products/components/Catalog/CategoryBreadcrumbs.vue'
import CatalogShowcase from '#Products/components/Catalog/Showcase/CatalogShowcase.vue'
import type { CategoryDto } from '#Products/models/Pim/CategoryDto'
import { categoryAncestorChain } from '#Products/support/categoryAncestorChain'
import NonePanel from '~/app/components/None/NonePanel.vue'

const props = defineProps<{
  category?: CategoryDto
  searchQuery?: string
}>()

const { routing } = useProducts()
const { byIds } = await useCategories()
const { resolve } = useCategoryDestination()

const resultCount = ref<number | null>(null)
const hasMore = ref(false)

const title = computed(() => {
  if (props.category) return props.category.name
  if (props.searchQuery) return `Поиск по «${props.searchQuery}»`
  return 'Каталог'
})

const resultCountLabel = computed(() => {
  if (resultCount.value == null) return ''
  const n = resultCount.value
  const suffix = hasMore.value ? '+' : ''
  return `${n}${suffix} ${pluralizeProduct(n)}`
})

const emptyMessage = computed(() =>
  props.searchQuery
    ? 'По запросу ничего не найдено'
    : 'В этой категории пока нет товаров',
)

const emptyHint = computed(() =>
  props.searchQuery
    ? 'Попробуйте другой запрос или вернитесь в каталог.'
    : 'Сбросьте фильтры или вернитесь к родительской категории.',
)

const parentCategory = computed(() => {
  if (!props.category) return undefined
  const ancestors = categoryAncestorChain(props.category, byIds.value)
  return ancestors.at(-1)
})

const backRoute = computed(() => {
  if (parentCategory.value) return resolve(parentCategory.value).route
  return routing.getCatalog().route
})

const backLabel = computed(() => {
  if (parentCategory.value) return `К ${parentCategory.value.name}`
  return 'В каталог'
})

function onResultCount(count: number) {
  resultCount.value = count
}

function onHasMore(value: boolean) {
  hasMore.value = value
}
</script>
